import { parseUserAgent } from "./FormatUtils.ts";

/**
 * The monitor the client is displayed on, as the backend expects it
 * (proto `types.MonitorInfo`). Note the spelling: the proto field is
 * `manufacture`, not `manufacturer` — the native `device:monitor:status`
 * payload uses the latter, so the mapping renames it.
 */
export interface MonitorInfo {
    manufacture?: string;
    product_code?: number;
    power?: string;
    source?: string;
    backlight?: number;
    additions?: string;   // proto's escape hatch for extra undocumented properties
}

/* see docs/EXTRACTED_COMMENTS.md#c171 */
export interface DevicePayload {
    device_id: string;
    username?: string;
    ip_address?: string;
    mac_address?: string;
    resolution?: string;
    browser?: string;
    browser_version?: string;
    os?: string;
    os_version?: string;
    rl?: string;          // runtime layer / shell — "Tauri" | "Electron" | "Samsung" | "WebMode"
    rl_version?: string;  // version of the app running in that layer
    monitor_info?: MonitorInfo;
}

export type ClientDeviceInfo = Omit<DevicePayload, "device_id">;

/**
 * Drop `undefined` values so a partial report never overwrites a known field
 * with nothing. The backend merges per field and treats an absent key as
 * "no change", but an explicit `undefined` would serialize as `null`.
 */
export function stripUndefined<T extends object>(value: T): Partial<T> {
    return Object.fromEntries(
        Object.entries(value).filter(([, v]) => v !== undefined),
    ) as Partial<T>;
}

/**
 * Fold a monitor report into what we already knew, keeping previous values for
 * anything the report didn't mention. Mirrors the backend's per-field merge.
 */
export function mergeMonitorInfo(current: MonitorInfo | null, report: MonitorInfo): MonitorInfo {
    return { ...(current ?? {}), ...stripUndefined(report) };
}

/**
 * Identity of the panel a report describes, from the EDID fields. Returns null
 * when the report doesn't identify one. `ClientInfo` upstream carries exactly
 * one monitor, so this is what decides whether two reports are about the same
 * screen or about two different ones.
 */
export function monitorIdentityOf(info: MonitorInfo): string | null {
    const {manufacture, product_code} = info;
    if (manufacture === undefined && product_code === undefined) return null;
    return `${manufacture ?? "?"}:${product_code ?? "?"}`;
}

/**
 * Pack the parts of a native monitor status that have no proto field of their
 * own into `additions`. Returns undefined when there is nothing extra to say.
 */
export function packMonitorAdditions(extras: Record<string, unknown>): string | undefined {
    const present = Object.entries(extras).filter(([, v]) => v !== undefined && v !== null && v !== "");
    if (present.length === 0) return undefined;
    return JSON.stringify(Object.fromEntries(present));
}

type UaBrand = { brand: string; version: string };

const isGreaseBrand = (brand: string): boolean => /not.?a.?brand/i.test(brand);

function normalizeBrand(brand: string): string {
    if (/edge|edg/i.test(brand)) return "Edge";
    if (brand === "Google Chrome") return "Chrome";
    return brand;
}

/* see docs/EXTRACTED_COMMENTS.md#c172 */
function pickBrand(list: UaBrand[]): { name?: string; version?: string } {
    const real = list.filter((e) => e && !isGreaseBrand(e.brand));
    const chosen = real.find((e) => !/chromium/i.test(e.brand)) ?? real[0];
    if (!chosen) return {};
    return { name: normalizeBrand(chosen.brand), version: chosen.version || undefined };
}

/* see docs/EXTRACTED_COMMENTS.md#c173 */
function normalizeOsName(platform: string): string | undefined {
    const p = (platform || "").trim();
    if (!p || p.toLowerCase() === "unknown") return undefined;
    switch (p.toLowerCase()) {
        case "macos":
            return "macOS";
        case "ios":
            return "iOS";
        case "chrome os":
        case "chromium os":
            return "Chrome OS";
        default:
            // "Windows", "Android", "Linux", … reported as-is.
            return p;
    }
}

/* see docs/EXTRACTED_COMMENTS.md#c174 */
function osVersionFor(osName: string, platformVersion: string): string | undefined {
    const pv = (platformVersion || "").trim();
    if (!pv) return undefined;
    if (osName === "Windows") {
        const major = parseInt(pv.split(".")[0] || "", 10);
        if (Number.isNaN(major)) return undefined;
        if (major >= 13) return "11";
        if (major >= 1) return "10";
        return undefined;
    }
    return pv;
}

/* see docs/EXTRACTED_COMMENTS.md#c175 */
export async function getClientDeviceInfo(): Promise<ClientDeviceInfo> {
    const info: ClientDeviceInfo = {};

    // Physical resolution of the display device.
    const w = window.screen?.width;
    const h = window.screen?.height;
    if (w && h) info.resolution = `${w}x${h}`;

    const uaData = (navigator as any).userAgentData;
    if (uaData && typeof uaData.getHighEntropyValues === "function") {
        try {
            const hints = await uaData.getHighEntropyValues([
                "platform",
                "platformVersion",
                "fullVersionList",
            ]);

            const brands: UaBrand[] = hints.fullVersionList ?? uaData.brands ?? [];
            const { name, version } = pickBrand(brands);
            if (name) info.browser = name;
            if (version) info.browser_version = version;

            const osName = normalizeOsName(hints.platform ?? uaData.platform ?? "");
            if (osName) {
                info.os = osName;
                const osVersion = osVersionFor(osName, hints.platformVersion ?? "");
                if (osVersion) info.os_version = osVersion;
            }

            return info;
        } catch {
            // Fall through to UA parsing.
        }
    }

    // Fallback: UA-string regex.
    const parsed = parseUserAgent();
    if (parsed.browser && parsed.browser !== "Unknown") info.browser = parsed.browser;
    if (parsed.version && parsed.version !== "Unknown") info.browser_version = parsed.version;
    if (parsed.osName) {
        info.os = parsed.osName;
        if (parsed.osVersion) info.os_version = parsed.osVersion;
    }

    return info;
}
